import React, { useState, useEffect, useCallback } from 'react';
import styles from './CreateSubscription.module.scss';
import TextField from '../../../components/textField/TextField';
import TextAreaField from '../../../components/textAreaField/TextAreaField';
import Dropdown from '../../../components/dropdown/Dropdown';
import CustomDatePicker from '../../../components/customDatePicker/CustomDatePicker';
import Button from '../../../components/button/Button';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import Icon from '../../../components/icon/Icon';
import { Tab, TabPanel, Tabs } from '../../../components/tabs/Tabs';
import SubscriptionDocuments from './SubscriptionDocuments';
import SubscriptionHistory from './SubscriptionHistory';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { formatPhoneNumber } from '../../../utils/stringUtils';

const CreateSubscription = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { subscriptionId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    startDate: null,
    endDate: null,
    status: true,
    isTrail: false,
    note: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [initialData, setInitialData] = useState(null);

  const fetchSubscription = useCallback(async () => {
    if (!subscriptionId) return;
    setIsLoading(true);
    try {
      const response = await axiosClient.get(`/subscription/${subscriptionId}`);
      const data = response.data?.subscription || response.data || response;
      const fetched = {
        name: data.name || '',
        startDate: data.startDate ? new Date(data.startDate) : null,
        endDate: data.endDate ? new Date(data.endDate) : null,
        status: data.status ?? true,
        isTrail: data.isTrail ?? false,
        note: data.note || '',
        contactName: data.contactName || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone ? formatPhoneNumber(data.contactPhone) : '',
      };
      setFormData(fetched);
      setInitialData(fetched);
      setDocuments(data.documents || []);
    } catch (error) {
      showToast(error.message || 'Failed to fetch subscription', 'error');
      navigate('/subscriptions');
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId, showToast, navigate]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name,
        startDate: formData.startDate.toISOString().split('T')[0],
        endDate: formData.endDate.toISOString().split('T')[0],
        status: formData.status,
        isTrail: formData.isTrail,
        note: formData.note,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone.replace(/\D/g, ''),
      };

      if (subscriptionId) {
        await axiosClient.patch(`/subscription/${subscriptionId}`, payload);
        showToast('Subscription updated successfully');
        await fetchSubscription();
      } else {
        const createResponse = await axiosClient.post('/subscription/create', payload);
        showToast('Subscription created successfully');
        const newId = createResponse.data?.id || createResponse.id;
        navigate(`/subscriptions/edit/${newId}`);
      }
    } catch (error) {
      showToast(error.message || 'Failed to save subscription', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const isFormDirty = () => {
    if (!initialData) return true;
    return (
      formData.name !== initialData.name ||
      formData.note !== initialData.note ||
      formData.status !== initialData.status ||
      formData.isTrail !== initialData.isTrail ||
      formData.contactName !== initialData.contactName ||
      formData.contactEmail !== initialData.contactEmail ||
      formData.contactPhone !== initialData.contactPhone ||
      (formData.startDate?.getTime() || null) !== (initialData.startDate?.getTime() || null) ||
      (formData.endDate?.getTime() || null) !== (initialData.endDate?.getTime() || null)
    );
  };

  return (
    <>
      {(isLoading || isSaving) && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Subscriptions', path: '/subscriptions' },
            { label: subscriptionId ? 'Edit Subscription' : 'Create Subscription' },
          ]}
        />
      </div>
      <Tabs defaultExpanded>
        <Tab><Icon name="file-text" /><span>Details</span></Tab>
        <Tab disabled={!subscriptionId}><Icon name="clipboard" /><span>Documents</span></Tab>
        <Tab disabled={!subscriptionId}><Icon name="file-text" /><span>History</span></Tab>

        <TabPanel>
        <div className={styles.container}>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <h2>Subscription Details</h2>

          <div className={styles.row}>
            <div className={styles.column50}>
              <TextField
                label="Name *"
                value={formData.name}
                onChange={(val) => handleChange('name', val)}
                placeholder="Enter subscription name"
                error={errors.name}
              />
            </div>
            <div className={styles.column25}>
              <Dropdown
                label="Status"
                value={formData.status ? 'true' : 'false'}
                onChange={(val) => handleChange('status', val === 'true')}
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
              />
            </div>
            <div className={styles.column25}>
              <Dropdown
                label="Type"
                value={formData.isTrail ? 'true' : 'false'}
                onChange={(val) => handleChange('isTrail', val === 'true')}
                options={[
                  { value: 'false', label: 'Regular' },
                  { value: 'true', label: 'Trial' },
                ]}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column100}>
              <TextAreaField
                label="Note"
                value={formData.note}
                onChange={(val) => handleChange('note', val)}
                placeholder="Enter note"
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column25}>
              <CustomDatePicker
                label="Start Date *"
                date={formData.startDate}
                onChange={(date) => handleChange('startDate', date)}
                showIcon
                maxDate={formData.endDate}
                error={errors.startDate}
              />
            </div>
            <div className={styles.column25}>
              <CustomDatePicker
                label="End Date *"
                date={formData.endDate}
                onChange={(date) => handleChange('endDate', date)}
                showIcon
                minDate={formData.startDate}
                error={errors.endDate}
              />
            </div>
          </div>

          <h2>Contact Details</h2>

          <div className={styles.row}>
            <div className={styles.column}>
              <TextField
                label="Contact Name"
                value={formData.contactName}
                onChange={(val) => handleChange('contactName', val)}
                placeholder="Enter contact name"
              />
            </div>
            <div className={styles.column}>
              <TextField
                label="Contact Email"
                value={formData.contactEmail}
                onChange={(val) => handleChange('contactEmail', val)}
                placeholder="Enter contact email"
              />
            </div>
            <div className={styles.column}>
              <TextField
                label="Contact Phone"
                value={formData.contactPhone}
                onChange={(val) => handleChange('contactPhone', formatPhoneNumber(val))}
                placeholder="000-000-0000"
              />
            </div>
          </div>

          {(!subscriptionId || isFormDirty()) && (
            <div className={styles.actions}>
              <Button
                label="Cancel"
                variant="white"
                size="medium"
                onClick={() => {
                  if (subscriptionId && initialData) {
                    setFormData(initialData);
                    setErrors({});
                  } else {
                    navigate('/subscriptions');
                  }
                }}
              />
              <Button
                label={subscriptionId ? 'Save Subscription' : 'Create Subscription'}
                variant="primary"
                size="medium"
                type="submit"
              />
            </div>
          )}
        </form>
        </div>
        </TabPanel>

        <TabPanel>
          <div className={styles.container}>
            <SubscriptionDocuments
              subscriptionId={subscriptionId}
              documents={documents}
              onRefresh={fetchSubscription}
            />
          </div>
        </TabPanel>

        <TabPanel>
          <div className={styles.container}>
            <SubscriptionHistory subscriptionId={subscriptionId} />
          </div>
        </TabPanel>
      </Tabs>
    </>
  );
};

export default CreateSubscription;
